import { Color, Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import {
  DrawerContentComponentProps,
  DrawerContentScrollView,
  DrawerHeaderProps,
} from '@react-navigation/drawer';
import React, { useContext, useEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import { AbsoluteRouteDetector } from '../../contexts/AbsoluteRouteDetector';

import { FixedSidebar } from './components/FixedSidebar';
import { MenuItem } from './components/MenuItem';
import { sidebarIcons, signoutMenuItem } from './constants';
import { AppMenuContext, AppMenuItem, AppMenuProvider } from './context';
import { HeaderPlaceholder } from './headerPlaceholder';

export const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
    backgroundColor: Color['neutral.white'],
    padding: Utils.Token.ToInt(Spacing, 'inline.xxxs'),
  },
  scrollInner: {
    flex: 1,
    justifyContent: 'space-between',
  },
  itemsContainer: {
    gap: Utils.Token.ToInt(Spacing, 'inline.nano'),
  },
});
const AppMenuBase: React.FC<DrawerContentComponentProps> = ({ descriptors, navigation }) => {
  const { items, setItems } = useContext(AppMenuContext);
  const { setLabels } = useContext(AbsoluteRouteDetector.Context);

  const itemList = useMemo(
    () =>
      items.map((item, index) => (
        <MenuItem item={item} key={index} navigation={navigation} expanded />
      )),
    [items, navigation]
  );

  useEffect(() => {
    const menuLabels = {};
    const menus: AppMenuItem[] = Object.entries(descriptors)
      .map(([_key, descriptor]) => {
        const label = descriptor.options?.title ?? 'Unknown';

        menuLabels[descriptor.route.name] = label;

        if (descriptor.options?.drawerItemStyle?.['display'] === 'none') {
          return null;
        }

        return {
          label,
          routeName: descriptor.route.name,
          routeParams: descriptor.route.params,
          icon: {
            name: sidebarIcons[descriptor.route.name] ?? 'Edit',
            size: 'sm',
          },
        };
      })
      .filter((item) => item !== null) as AppMenuItem[];

    setLabels(menuLabels);
    setItems(menus);
  }, [descriptors, setItems, setLabels]);

  return (
    <DrawerContentScrollView
      style={styles.scrollContainer}
      contentContainerStyle={styles.scrollInner}>
      <View style={styles.itemsContainer}>{itemList}</View>
      <MenuItem item={signoutMenuItem} navigation={navigation} expanded />
    </DrawerContentScrollView>
  );
};

export const AppMenu = Object.assign(AppMenuBase, {
  Context: AppMenuContext,
  Provider: AppMenuProvider,
  HeaderPlaceholder,
  FixedSidebar,
  CombinedHeader: (props: DrawerHeaderProps) => {
    return (
      <>
        <HeaderPlaceholder {...props} />
        <FixedSidebar {...props} />
      </>
    );
  },
});
